export const getOptimizedBlob = async (croppieInstance, maxKb = 20) => {
  let currentQuality = 0.9; // Start with a high quality
  
  // 1. Get the initial crop
  let blob = await croppieInstance.result({
    type: 'blob',
    size: { width: 450, height: 450 },
    format: 'webp',
    quality: currentQuality
  });

  // 2. Loop and degrade quality until it fits the size constraint
  // (We stop at 0.1 quality to prevent an infinite loop on impossible images)
  while (blob.size > maxKb * 1024 && currentQuality > 0.1) {
    currentQuality -= 0.1; // Drop quality by 10% each attempt
    
    blob = await croppieInstance.result({
      type: 'blob',
      size: { width: 450, height: 450 },
      format: 'webp',
      quality: Number(currentQuality.toFixed(1)) // keep math clean
    });
  }

  console.log(`Final size: ${(blob.size / 1024).toFixed(2)} KB at ${currentQuality} quality`);
  return blob; // Ready for Firebase!
}

/**
 * Compresses an image file to WebP format, scaling it down to a max width/height
 * while maintaining aspect ratio, and looping to hit a target file size.
 * * @param {File} file - The original uploaded image file
 * @param {number} maxKb - The target maximum file size in Kilobytes (e.g., 20)
 * @returns {Promise<Blob>} - The optimized WebP blob
 */
export const getOptimizedImageNoCrop = async (file, maxKb = 20, MAX_DIMENSION = 450) => {
  // If it's a GIF, skip compression or handle differently
  if (file.type === 'image/gif') {
    return await compressGif(file, maxKb, MAX_DIMENSION);
  }
  console.log('Original file size in KB:', file.size / 1024);
  if (file.size <= maxKb * 1024) {
    // console.log('File is already under the size limit, no optimization needed.');
    return file; // No need to optimize
  }
  return new Promise((resolve, reject) => {
    // 1. Create an image element to read the file dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = async () => {
      // Clean up memory
      URL.revokeObjectURL(objectUrl);

      // 2. Calculate the new dimensions while maintaining aspect ratio
      let { width, height } = img;
      // const MAX_DIMENSION = 450;

      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width > height) {
          height = Math.round((height * MAX_DIMENSION) / width);
          width = MAX_DIMENSION;
        } else {
          width = Math.round((width * MAX_DIMENSION) / height);
          height = MAX_DIMENSION;
        }
      }

      // 3. Draw the resized image onto a native HTML canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Helper function to turn the canvas into a Blob
      const getCanvasBlob = (quality) => {
        return new Promise((res) => {
          canvas.toBlob((blob) => res(blob), 'image/webp', quality);
        });
      };

      // 4. The Quality Loop (Same logic as before!)
      let currentQuality = 0.9;
      let blob = await getCanvasBlob(currentQuality);

      // Loop until it's under the maxKb limit, stopping at a floor of 0.5 to prevent it from looking awful
      while (blob.size > maxKb * 1024 && currentQuality > 0.5) {
        currentQuality -= 0.1;
        currentQuality = Math.round(currentQuality * 10) / 10; // Fix JS floating point math
        
        blob = await getCanvasBlob(currentQuality);
      }

      console.log(`Resized to ${width}x${height}. Final size: ${(blob.size / 1024).toFixed(2)} KB at ${currentQuality} quality`);
      resolve(blob);
    };

    img.onerror = (error) => {
      reject(new Error("Failed to load image for compression."));
    };

    // Trigger the load event
    img.src = objectUrl;
  });
};

// import GIF from 'gif.js/dist/gif';
// import { GifReader } from 'omggif';

// const compressGif = async (file, maxKb) => {
//   const arrayBuffer = await file.arrayBuffer();
//   const reader = new GifReader(new Uint8Array(arrayBuffer));
//   const numFrames = reader.numFrames();
//   const { width: origWidth, height: origHeight } = reader;

//   // 1. Dimensions
//   const MAX_DIMENSION = 400; // Dropped slightly to help hit 20KB
//   const ratio = Math.min(MAX_DIMENSION / origWidth, MAX_DIMENSION / origHeight, 1);
//   const width = Math.round(origWidth * ratio);
//   const height = Math.round(origHeight * ratio);

//   // 2. Dynamic Step calculation
//   // If the file is way over target, skip more frames.
//   let step = 1;
//   if (file.size > maxKb * 1024 * 5) step = 3;
//   else if (file.size > maxKb * 1024 * 2) step = 2;

//   const canvas = document.createElement('canvas');
//   canvas.width = width;
//   canvas.height = height;
//   const ctx = canvas.getContext('2d');

//   const tempCanvas = document.createElement('canvas');
//   tempCanvas.width = origWidth;
//   tempCanvas.height = origHeight;
//   const tempCtx = tempCanvas.getContext('2d');

//   const gif = new GIF({
//     workers: 2,
//     quality: 25, // Higher = smaller file size (aggressive color grouping)
//     width: width,
//     height: height,
//     workerScript: '/gif.worker.js',
//     transparent: 'rgba(0,0,0,0)'
//   });

//   let accumulatedDelay = 0;

//   for (let i = 0; i < numFrames; i++) {
//     const frameInfo = reader.frameInfo(i);
//     // Standard GIF delay is 1/100s. We convert to ms.
//     // Most browsers floor 0ms or 10ms to 100ms, so we use 100 as a safe default.
//     const frameDelay = frameInfo.delay > 0 ? frameInfo.delay * 10 : 100;

//     accumulatedDelay += frameDelay;

//     // Only "record" the frame if we hit our step
//     if (i % step === 0 || i === numFrames - 1) {
//       tempCtx.clearRect(0, 0, origWidth, origHeight);
//       const imageData = tempCtx.createImageData(origWidth, origHeight);
//       reader.decodeAndBlitFrameRGBA(i, imageData.data);
//       tempCtx.putImageData(imageData, 0, 0);

//       ctx.clearRect(0, 0, width, height);
//       ctx.drawImage(tempCanvas, 0, 0, width, height);

//       gif.addFrame(ctx, { 
//         copy: true, 
//         delay: accumulatedDelay, // Use the total time passed since last kept frame
//         disposal: 2 
//       });

//       accumulatedDelay = 0; // Reset clock for the next chunk of frames
//     }
//   }

//   return new Promise((resolve) => {
//     gif.on('finished', (blob) => resolve(blob));
//     gif.render();
//   });
// };